import type { Game } from "@/api";
import { MINECRAFT_JAVA_VERSIONS } from "@mars-host/shared";

export const DEFAULT_SERVER_LIMIT = 1;
export const SERVER_START_COST = 5;
export const SERVER_HOURLY_COST = 5;
export const SERVER_SLOT_COST = 1000;
export const BACKUP_SLOT_COST = 1000;

/** Арты игр лежат в apps/web/public/games (официальные key-art Minecraft и Steam). */
export const gameOptions: Array<{
  value: Game;
  label: string;
  image: string;
  defaultVersion: string;
  versions: readonly string[];
  cores: string[];
}> = [
  {
    value: "minecraft_java",
    label: "Minecraft Java",
    image: "/games/minecraft_java.jpg",
    defaultVersion: "1.21.4",
    versions: MINECRAFT_JAVA_VERSIONS,
    cores: ["vanilla", "paper", "leaf", "fabric", "forge", "neoforge"],
  },
];

export const statusLabels: Record<string, string> = {
  queued_install: "Ожидает установки",
  queued_start: "Ожидает запуска",
  queued_stop: "Ожидает остановки",
  queued_delete: "Ожидает удаления",
  installing: "Установка",
  stopped: "Остановлен",
  starting: "Запускается",
  running: "В сети",
  stopping: "Останавливается",
  deleting: "Удаляется",
  failed: "Ошибка",
};

export const nodeStatusLabels: Record<string, string> = {
  online: "Доступна для размещения",
  offline: "Нет подключения",
  draining: "Выводится из распределения",
  disabled: "Отключена для размещения",
};

export const transitionalStatuses = new Set([
  "queued_install",
  "queued_start",
  "queued_stop",
  "queued_delete",
  "installing",
  "starting",
  "stopping",
  "deleting",
]);

export function gameLabel(game: Game): string {
  return gameOptions.find((option) => option.value === game)?.label ?? game;
}

export const ledgerSourceLabels: Record<string, string> = {
  signup: "Приветственный бонус",
  daily_bonus: "Ежедневный бонус",
  ad_watch: "Награда за рекламу",
  server_charge: "Оплата работы сервера",
  server_slot: "Покупка слота сервера",
  backup_slot: "Покупка слота бэкапов",
  admin_adjust: "Корректировка администратором",
  refund: "Возврат кредитов",
};

/** Пояснения к сборкам доступны при создании и изменении сервера. */
export const coreOptions = [
  { value: "vanilla", label: "Vanilla", description: "Обычный Minecraft без плагинов и модов." },
  {
    value: "paper",
    label: "Paper",
    description:
      "Сервер с поддержкой плагинов. Подойдёт, если хотите добавить команды и игровые функции.",
  },
  {
    value: "leaf",
    label: "Leaf",
    description: "Оптимизированная сборка на основе Paper с поддержкой плагинов Paper и Bukkit.",
  },
  {
    value: "fabric",
    label: "Fabric",
    description: "Для модов, рассчитанных на Fabric. Проверьте требования вашей сборки модов.",
  },
  {
    value: "forge",
    label: "Forge",
    description: "Для модов, рассчитанных на Forge. Выбирайте под нужную сборку модов.",
  },
  {
    value: "neoforge",
    label: "NeoForge",
    description: "Для модов, рассчитанных на NeoForge. Проверьте совместимость сборки модов.",
  },
];

export function supportsPlugins(core: string): boolean {
  return core === "paper" || core === "leaf";
}

export function serverStatusHint(status: string, queuePosition?: number | null): string {
  const position = queuePosition != null ? ` Место в очереди: №${queuePosition}.` : "";
  switch (status) {
    case "queued_install":
      return `Ожидаем оборудование для установки.${position} Установка и запуск начнутся автоматически.`;
    case "queued_start":
      return `Сервер ожидает своей очереди и доступных ресурсов оборудования.${position} Сервер запустится автоматически; оплаченный час ещё не начался.`;
    case "installing":
      return "Устанавливаем выбранную сборку. Затем сервер запустится автоматически. Дополнительных действий не требуется.";
    case "starting":
      return "Сервер запускается. Дождитесь состояния «В сети», прежде чем подключаться. Ход запуска виден в консоли.";
    case "queued_stop":
      return "Остановка поставлена в очередь. Дождитесь состояния «Остановлен», прежде чем менять файлы или запускать сервер снова.";
    case "stopping":
      return "Сервер завершает работу. Дождитесь состояния «Остановлен».";
    case "queued_delete":
      return "Удаление поставлено в очередь. После его выполнения сервер и файлы исчезнут из панели.";
    case "deleting":
      return "Удаляем сервер и его файлы. После завершения вернём вас к списку серверов.";
    case "failed":
      return "Операция завершилась с ошибкой. Откройте консоль, проверьте причину и повторите запуск после исправления.";
    case "stopped":
      return "Сервер выключен, подключение игроков недоступно. Для игры запустите сервер.";
    case "running":
      return "Сервер запущен и доступен для подключения игроков.";
    default:
      return "";
  }
}
